import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

/**
 * Guest invitation links for the Reddit photo-flow pilot point at the apex root.
 * The one-card Try experience (before any account wall) is the intended first
 * screen, so a guest arriving with exactly this campaign tuple is sent to /try.
 *
 * Trust decision, kept in one place:
 *  - Only the finite, already-approved Reddit placements below qualify. Each of
 *    the four utm keys must appear exactly once, so a duplicated or conflicting
 *    key is treated as an edited link and stays on the ordinary landing page.
 *  - The target is the fixed internal path /try, never taken from the URL, so
 *    this cannot become an open redirect.
 *  - Search and hash are passed through untouched, which keeps the labels that
 *    the Essential Only handoff reads (campaignLabels.ts). Nothing new is read
 *    or stored.
 *  - Auth callback parameters outrank the campaign: if any appear in the query
 *    or the fragment, the visit is a sign-in return and is never rerouted.
 * Signed-in visitors never reach this: PublicRoute sends them to /app first.
 * Changing a label here without campaignLabels.ts would break the handoff.
 */
const PILOT_TUPLE = {
  utm_source: ['reddit'],
  utm_medium: ['post'],
  utm_campaign: ['week01_photo_flow_pilot'],
  utm_content: ['betatesters', 'testmyapp'],
} as const;

// Parameters Supabase puts on a sign-in or confirmation return (query or fragment).
const AUTH_CALLBACK_KEYS = [
  'code',
  'error',
  'error_code',
  'error_description',
  'access_token',
  'refresh_token',
  'token_hash',
  'provider_token',
  'type',
];

function hasAuthCallbackParams(params: URLSearchParams): boolean {
  return AUTH_CALLBACK_KEYS.some((key) => params.has(key));
}

/** `/try` plus the untouched search and hash for a recognised pilot link, else null. */
export function pilotTryDestination(search: string, hash: string): string | null {
  const query = new URLSearchParams(search);
  if (hasAuthCallbackParams(query)) return null;
  if (hasAuthCallbackParams(new URLSearchParams(hash.replace(/^#/, '')))) return null;
  for (const [key, allowed] of Object.entries(PILOT_TUPLE)) {
    const values = query.getAll(key);
    if (values.length !== 1) return null;
    if (!(allowed as readonly string[]).includes(values[0].toLowerCase())) return null;
  }
  return `/try${search}${hash}`;
}

/** Wraps the guest landing page; `replace` keeps Back from returning into the redirect. */
export function PilotTryEntry({ children }: { children: ReactNode }) {
  const { search, hash } = useLocation();
  const destination = pilotTryDestination(search, hash);
  if (destination) return <Navigate to={destination} replace />;
  return <>{children}</>;
}
